import { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import { useAuth } from '@/hooks/use-auth';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

/**
 * This component handles incoming authentication tokens from redirect URLs
 * It should be placed on routes that receive incoming external authentications
 */
export default function AuthRedirectHandler() {
  const [, params] = useRoute('/auth-redirect');
  const [, navigate] = useLocation();
  const { loginWithToken } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processAuthToken = async () => {
      setIsProcessing(true);
      setError(null);

      // Get the token from URL parameters
      const searchParams = new URLSearchParams(window.location.search);
      const token = searchParams.get('auth_token');

      if (!token) {
        setError('No authentication token provided');
        setIsProcessing(false);
        return;
      }

      try {
        // Attempt to log in with the token
        const user = await loginWithToken(token);

        if (!user) {
          setError('Invalid or expired authentication token');
          setIsProcessing(false);
          return;
        }

        // Redirect to home page or a destination URL if specified
        const destination = searchParams.get('redirect_to') || '/';
        navigate(destination);
      } catch (err) {
        console.error('Error processing auth token:', err);
        setError('Failed to authenticate with the provided token');
        setIsProcessing(false);
      }
    };

    if (window.location.pathname === '/auth-redirect') {
      processAuthToken();
    }
  }, [loginWithToken, navigate]);

  if (!isProcessing && !error) {
    return null;
  }

  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <div className="w-full max-w-md">
        {isProcessing && !error ? (
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Authenticating...</h2>
            <p className="text-muted-foreground">Please wait while we log you in...</p>
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTitle>Authentication Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
      </div>
    </div>
  );
}
